import apiService from './api';

export interface QualityAnalysisData {
  pesee: number;
  grainnage?: string; // Nouveau champ pour le grainnage (N/kg)
  humidite?: number; // Taux d'humidité en %
  quality?: number; // Qualité numérique
  quality_level?: string; // Niveau de qualité (EXCELLENT, BON, MOYEN, FAIBLE, TRES_FAIBLE)
  quality_decision: 'ACCEPTE' | 'ACCEPTE_RESERVE' | 'REFUSE';
  observations?: string;
  // Anciens champs (optionnels pour compatibilité)
  moisture?: number;
  proteins?: number;
  quality_numbers?: string;
  refraction_hum?: number;
  refraction_qlt?: number;
  hectoliter_weight?: number;
  impurities?: number;
  broken_grains?: number;
  germinated_grains?: number;
}

export interface QualityAnalysisResponse {
  id: string;
  pesee: number;
  pesee_reference: string;
  grainnage?: string; // Nouveau champ pour le grainnage (N/kg)
  humidite?: number; // Taux d'humidité en %
  quality?: number; // Qualité numérique
  quality_level?: string; // Niveau de qualité (EXCELLENT, BON, MOYEN, FAIBLE, TRES_FAIBLE)
  quality_decision: 'ACCEPTE' | 'ACCEPTE_RESERVE' | 'REFUSE';
  quality_decision_display: string;
  quality_score?: number;
  observations?: string;
  analysed_by?: number;
  analysed_by_username?: string;
  analysed_at: string;
  created_at: string;
  updated_at: string;
  // Anciens champs (optionnels pour compatibilité)
  moisture?: number;
  proteins?: number;
  quality_numbers?: string;
  refraction_hum?: number;
  refraction_qlt?: number;
  hectoliter_weight?: number;
  impurities?: number;
  broken_grains?: number;
  germinated_grains?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

class QualityAnalysisService {
  private baseUrl = '/weighing/api/analyses-qualite/';

  /**
   * Créer une analyse qualité
   */
  async createQualityAnalysis(data: QualityAnalysisData): Promise<ApiResponse<QualityAnalysisResponse>> {
    try {
     
      const response = await apiService.post(this.baseUrl, data);
      
      return {
        success: true,
        message: response.data.message || 'Analyse qualité créée avec succès',
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la création de l\'analyse qualité',
        data: null as any
      };
    }
  }

  /**
   * Créer une analyse qualité pour une pesée spécifique
   */
  async createQualityAnalysisForPesee(peseeId: number, data: Omit<QualityAnalysisData, 'pesee'>): Promise<ApiResponse<QualityAnalysisResponse>> {
    try {
      const response = await apiService.post(`${this.baseUrl}create_for_pesee/`, {
        pesee_id: peseeId,
        ...data
      });
      return {
        success: true,
        message: response.data.message || 'Analyse qualité créée avec succès',
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la création de l\'analyse qualité',
        data: null as any
      };
    }
  }

  /**
   * Récupérer une analyse qualité par ID
   */
  async getQualityAnalysis(id: string): Promise<ApiResponse<QualityAnalysisResponse>> {
    try {
      const response = await apiService.get(`${this.baseUrl}${id}/`);
      
      return {
        success: true,
        message: 'Analyse qualité récupérée avec succès',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la récupération de l\'analyse qualité',
        data: null as any
      };
    }
  }

  /**
   * Mettre à jour une analyse qualité
   */
  async updateQualityAnalysis(id: string, data: Partial<QualityAnalysisData>): Promise<ApiResponse<QualityAnalysisResponse>> {
    try {
      
      const response = await apiService.patch(`${this.baseUrl}${id}/`, data);
      return {
        success: true,
        message: response.data.message || 'Analyse qualité mise à jour avec succès',
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la mise à jour de l\'analyse qualité',
        data: null as any
      };
    }
  }

  /**
   * Supprimer une analyse qualité
   */
  async deleteQualityAnalysis(id: string): Promise<ApiResponse<null>> {
    try {
      await apiService.delete(`${this.baseUrl}${id}/`);
      
      return {
        success: true,
        message: 'Analyse qualité supprimée avec succès',
        data: null
      };
    } catch (error: any) {
      console.error('❌ deleteQualityAnalysis: Erreur:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la suppression de l\'analyse qualité',
        data: null
      };
    }
  }

  /**
   * Récupérer une analyse qualité par ID de pesée
   */
  async getQualityAnalysisByPeseeId(peseeId: number): Promise<ApiResponse<QualityAnalysisResponse>> {
    try {
     
      const response = await apiService.get(`${this.baseUrl}?pesee=${peseeId}`);
      
    
      // Si on a des résultats, prendre le premier
      if (response.data.results && response.data.results.length > 0) {
        return {
          success: true,
          message: 'Analyse qualité récupérée avec succès',
          data: response.data.results[0]
        };
      } else {
        return {
          success: false,
          message: 'Aucune analyse qualité trouvée pour cette pesée',
          data: null as any
        };
      }
    } catch (error: any) {
      console.error('❌ getQualityAnalysisByPeseeId: Erreur:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la récupération de l\'analyse qualité',
        data: null as any
      };
    }
  }

  /**
   * Récupérer les analyses qualité avec filtres
   */
  async getQualityAnalyses(params?: {
    quality_decision?: string;
    pesee__statut?: string;
    search?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{
    count: number;
    next: string | null;
    previous: string | null;
    results: QualityAnalysisResponse[];
  }>> {
    try {
      const response = await apiService.get(this.baseUrl, { params });
      
      return {
        success: true,
        message: 'Analyses qualité récupérées avec succès',
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ getQualityAnalyses: Erreur:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la récupération des analyses qualité',
        data: {
          count: 0,
          next: null,
          previous: null,
          results: []
        }
      };
    }
  }
}

const qualityAnalysisService = new QualityAnalysisService();
export default qualityAnalysisService;
